import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { VIDEO_URL_UNSUPPORTED_FORMAT_ERROR_MESSAGE_ID } from 'modules/campaigns/modules/creative-creator/config/thumbnailValidationConsts';
import ValidationError from 'modules/errors/ValidationError';
import { addIndication, INDICATION_TYPES, removeIndication } from 'modules/taboola-common-frontend-modules/Indications';
import { useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { GTM_EVENTS, gtmTracker } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useCreativesApi } from 'services/api';
import { errorMessagesUtils } from 'services/utils';
import { CROPPING_MODAL_COMPONENT_NAME, OUTCROPPING_COMPONENT_NAME } from '../constants/croppingModalGtmConstants';
import { getImageMaxZoomBySize } from '../utils/cropDataUtils';

export const UNSUPPORTED_FILE_TYPE_VALIDATION_ERROR = new ValidationError(
    'Unsupported file type',
    VIDEO_URL_UNSUPPORTED_FORMAT_ERROR_MESSAGE_ID
);

export const sendCroppingModalTrackingEvent = data => {
    gtmTracker.trackEvent(GTM_EVENTS.USABILITY, { component: CROPPING_MODAL_COMPONENT_NAME, ...data });
};

const generateErrorIndication = (errorObject = {}) => {
    const flowErrorMessage = (
        <FormattedMessage
            id={`cropping.content.replace.image.upload.error.${errorObject.status}`}
            defaultMessage="Please try again or contact support"
        />
    );
    return {
        message: errorMessagesUtils.getErrorMessage(errorObject, flowErrorMessage),
        type: INDICATION_TYPES.ERROR,
        highlight: (
            <FormattedMessage
                id="cropping.content.replace.image.upload.error"
                defaultMessage="Unable to upload the image"
            />
        ),
    };
};

export const useCropImageControlsState = ({
    src,
    originalSrc,
    updateSrc,
    setSrcHistory,
    ratio,
    setKey,
    isOutcroppingEnabled,
    srcHistory,
}) => {
    const { getSingleCropAreaSize } = useCreativesApi();
    const dispatch = useDispatch();

    const { onChange: setInitialCroppedAreaPixels } = useFormFieldValue({
        field: 'initialCroppedAreaPixels',
    });
    const { value: isFromCloudinary, onChange: setIsFromCloudinary } = useFormFieldValue({
        field: 'isFromCloudinary',
    });
    const { value: cloudinaryInitialCroppedAreaPixels, onChange: setCloudinaryInitialCroppedAreaPixels } =
        useFormFieldValue({
            field: 'cloudinaryInitialCroppedAreaPixels',
        });
    const { value: replacedImageUrl, onChange: setReplacedImageUrl } = useFormFieldValue({
        field: `replacedImageUrl`,
    });
    const { onChange: setCroppedAreaPixels } = useFormFieldValue({
        field: 'croppedAreaPixels',
    });
    const { onChange: setReplacedImageMaxZoom } = useFormFieldValue({
        field: 'replacedImageMaxZoom',
    });

    const [isUploadingReplacedImage, setIsUploadingReplacedImage] = useState(false);

    const onResetCrop = useCallback(() => {
        if (replacedImageUrl) {
            setReplacedImageUrl(null);
            setReplacedImageMaxZoom(null);
        }

        if (!isFromCloudinary) {
            setInitialCroppedAreaPixels(cloudinaryInitialCroppedAreaPixels);
        }

        setCroppedAreaPixels(null);
        setKey(null);
        sendCroppingModalTrackingEvent({
            value: 'reset crop',
        });
    }, [
        setKey,
        isFromCloudinary,
        setInitialCroppedAreaPixels,
        cloudinaryInitialCroppedAreaPixels,
        replacedImageUrl,
        setReplacedImageUrl,
        setCroppedAreaPixels,
        setReplacedImageMaxZoom,
    ]);

    const showErrorIndication = useCallback(
        error => {
            dispatch(addIndication(generateErrorIndication(error)));
        },
        [dispatch]
    );

    const onReplaceImage = useCallback(
        async urlResolver => {
            dispatch(removeIndication({ type: INDICATION_TYPES.ERROR }));
            setIsUploadingReplacedImage(true);
            try {
                const { url } = await urlResolver();
                setReplacedImageUrl(url);

                const cloudinaryData = await getSingleCropAreaSize(url, ratio);
                const cloudinaryCroppedArea = cloudinaryData?.resize?.[0];
                setCloudinaryInitialCroppedAreaPixels(cloudinaryCroppedArea);
                setIsFromCloudinary(true);

                const imageMaxZoom = await getImageMaxZoomBySize(url);
                setReplacedImageMaxZoom(imageMaxZoom);

                setInitialCroppedAreaPixels(cloudinaryCroppedArea);
                setCroppedAreaPixels(null);
                setKey(null);
                sendCroppingModalTrackingEvent({
                    value: 'replace image - upload - success',
                });
            } catch (error) {
                showErrorIndication(error);
                sendCroppingModalTrackingEvent({
                    value: 'replace image - upload - error',
                    additionalEventInfo: error,
                });
            }
            setIsUploadingReplacedImage(false);
        },
        [
            dispatch,
            showErrorIndication,
            getSingleCropAreaSize,
            setReplacedImageUrl,
            setCloudinaryInitialCroppedAreaPixels,
            setReplacedImageMaxZoom,
            setInitialCroppedAreaPixels,
            setIsFromCloudinary,
            setKey,
            setCroppedAreaPixels,
            ratio,
        ]
    );

    const onResetOutcropping = useCallback(() => {
        updateSrc(originalSrc);
        setSrcHistory([]);
        sendCroppingModalTrackingEvent({
            component: `${OUTCROPPING_COMPONENT_NAME}: Reset Clicked`,
            value: src,
        });
    }, [originalSrc, setSrcHistory, src, updateSrc]);

    return {
        onResetClick: isOutcroppingEnabled && srcHistory?.length > 0 ? onResetOutcropping : onResetCrop,
        showErrorIndication,
        onReplaceImage,
        isUploadingReplacedImage,
    };
};
